import { createLogger } from "@apps-logs/browser";

const missingPublicKey = "MISSING_PUBLIC_KEY";
const publicKey = process.env.VUE_APP_APPSLOGS_PUBLIC_KEY || missingPublicKey;
const dsn = process.env.VUE_APP_APPSLOGS_DSN;
const appsLogsEnabled = process.env.VUE_APP_APPSLOGS_ENABLED === "true";

function createNoopLogger() {
  const noop = () => {};
  const asyncNoop = () => Promise.resolve();

  return {
    getClientId: () => "apps-logs-disabled-client",
    getSessionId: () => "apps-logs-disabled-session",
    log: noop,
    debug: noop,
    info: noop,
    warn: noop,
    error: noop,
    fatal: noop,
    event: noop,
    screen: noop,
    action: noop,
    snapshot: noop,
    auth: noop,
    setAuthProvider: noop,
    identify: noop,
    resetUser: noop,
    setTags: () => ({}),
    setContext: noop,
    destroy: noop,
    flush: asyncNoop,
  };
}

function safeLocationHref() {
  return typeof window !== "undefined" && window.location
    ? window.location.href
    : "";
}

function safeUserAgent() {
  return typeof window !== "undefined" && window.navigator
    ? window.navigator.userAgent
    : "";
}

function resolveAuthHelper() {
  try {
    // Lazy-load auth to avoid the circular import:
    // helpers/auth -> plugins/appsLogsCore -> helpers/auth.
    // During early plugin evaluation webpack may return a partial module, so all
    // callers must still guard method access.
    const authModule = require("@/helpers/auth");
    return authModule && (authModule.default || authModule);
  } catch (error) {
    return null;
  }
}

function callAuth(methodName, fallback = "") {
  const auth = resolveAuthHelper();

  if (!auth || typeof auth[methodName] !== "function") {
    return fallback;
  }

  try {
    return auth[methodName]();
  } catch (error) {
    return fallback;
  }
}

function getAuthMeta(key) {
  const auth = resolveAuthHelper();

  if (!auth || typeof auth.getMeta !== "function") {
    return undefined;
  }

  try {
    return auth.getMeta(key);
  } catch (error) {
    return undefined;
  }
}

function safeAuthSnapshot() {
  return {
    hasAccessToken: !!callAuth("getAccessToken", ""),
    hasRefreshToken: !!callAuth("getRefreshToken", ""),
    accessTokenExpiresAt: callAuth("getAccessTokenExpiresAt", ""),
    refreshTokenExpiresAt: callAuth("getRefreshTokenExpiresAt", ""),
    clientType: callAuth(
      "getClientType",
      process.env.VUE_APP_CLIENT_TYPE || "browser"
    ),
    deviceId: callAuth("getDeviceId", ""),
    authVersion: getAuthMeta("auth_version"),
  };
}

function safeCurrentRoute() {
  const location = typeof window !== "undefined" ? window.location : null;

  return {
    urlPath: location ? location.pathname : "",
    urlSearch: location ? location.search : "",
    urlHash: location ? location.hash : "",
  };
}

function routeToDiagnostic(route, prefix = "route") {
  if (!route) return {};

  return {
    [`${prefix}Name`]: route.name || "",
    [`${prefix}Path`]: route.path || "",
    [`${prefix}FullPath`]: route.fullPath || route.path || "",
    [`${prefix}RequiresAuth`]: !!(route.meta && route.meta.requiresAuth),
    [`${prefix}RequiresGuest`]: !!(route.meta && route.meta.requiresGuest),
  };
}

function compactObject(value = {}) {
  return Object.keys(value).reduce((result, key) => {
    const item = value[key];
    if (item !== undefined && item !== null && item !== "") {
      result[key] = item;
    }
    return result;
  }, {});
}

function getClientContext() {
  const clientType = callAuth(
    "getClientType",
    process.env.VUE_APP_CLIENT_TYPE || "browser"
  );
  const deviceId = callAuth("getDeviceId", "");

  return {
    clientType,
    isKiosk: clientType === "kiosk",
    deviceId,
  };
}

export function syncAppsLogsClientContext(options = {}) {
  const targetLogger = options.logger || logger;
  const context = getClientContext();

  if (!targetLogger) {
    return context;
  }

  targetLogger.setTags({
    clientType: context.clientType,
    isKiosk: context.isKiosk ? "true" : "false",
  });

  targetLogger.setContext("client", context);

  if (options.emitEvent) {
    targetLogger.auth(options.eventName || "auth.client_context_synced", {
      clientType: context.clientType,
      isKiosk: context.isKiosk,
      deviceId: context.deviceId,
    });
  }

  return context;
}

export function createAttemptId(prefix = "attempt") {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

export function getAppsLogsDiagnosticContext(options = {}) {
  const authSnapshot = safeAuthSnapshot();
  const clientContext = getClientContext();
  const actor = options.user ? toLoggerUser(options.user) : undefined;

  return compactObject({
    ...safeCurrentRoute(),
    ...routeToDiagnostic(options.route || options.to, "route"),
    ...routeToDiagnostic(options.from, "from"),
    ...routeToDiagnostic(options.to, "to"),
    ...authSnapshot,
    ...clientContext,
    sessionId:
      logger && logger.getSessionId ? logger.getSessionId() : undefined,
    clientId: logger && logger.getClientId ? logger.getClientId() : undefined,
    actor,
    ...options.extra,
  });
}

export function logAuthEvent(event, metadata = {}, options = {}) {
  const targetLogger = options.logger || logger;
  const level = options.level || "auth";
  const diagnosticContext = getAppsLogsDiagnosticContext({
    route: options.route,
    from: options.from,
    to: options.to,
    user: options.user,
    extra: metadata,
  });

  if (typeof targetLogger[level] === "function") {
    targetLogger[level](event, diagnosticContext);
    return diagnosticContext;
  }

  targetLogger.auth(event, diagnosticContext);
  return diagnosticContext;
}

export function getErrorDiagnostic(error = {}) {
  const response = error.response || {};
  const data = response.data || {};

  return compactObject({
    name: error.name,
    message: error.message,
    status: response.status,
    statusText: response.statusText,
    code:
      (data.error && data.error.code) ||
      data.code ||
      (data.data && data.data.error && data.data.error.code) ||
      (data.data && data.data.code) ||
      error.code,
  });
}

function createAppsLogger() {
  if (!appsLogsEnabled || !dsn || publicKey === missingPublicKey) {
    return createNoopLogger();
  }

  const logger = createLogger({
    dsn,
    appKey: publicKey,
    environment: process.env.NODE_ENV || "development",
    release:
      process.env.VUE_APP_RELEASE || process.env.VUE_APP_VERSION || "local-dev",

    captureConsole: true,
    consoleLevels: ["warn", "error"],
    captureErrors: true,
    captureNetwork: true,
    captureWebSockets: true,
    captureLifecycle: true,
    captureIdle: true,
    captureStorage: true,
    captureUnmatchedStorageKeys: false,
    storageKeyMode: "category",
    storageKeys: [
      "sq_auth_*",
      "apps_logs_*",
      "client_type",
      "device_identification",
      "device_*",
    ],
    snapshotOn: [
      "app.pageshow",
      "app.page_visible",
      "app.resume",
      "app.online",
      "app.offline",
    ],
    ignoredUrls: [/\/app$/, "/health"],
    redactKeys: [
      "sq_auth_refresh_token",
      "sq_auth_access_token_expires_at",
      "sq_auth_refresh_token_expires_at",
    ],
    slowRequestThresholdMs: 3000,
    flushIntervalMs: 5000,
    maxBatchSize: 50,
    maxQueueSize: 500,
    dedupeWindowMs: 10000,
    maxEventsPerMinute: 120,
  });

  logger.setAuthProvider({
    getSnapshot: safeAuthSnapshot,
  });

  syncAppsLogsClientContext({ logger });

  logger.snapshot("app.boot_snapshot", {
    url: safeLocationHref(),
    userAgent: safeUserAgent(),
  });

  return logger;
}

const logger = createAppsLogger();

export function toLoggerUser(user = {}) {
  return {
    id: user.id || user.uuid || user.user_id || user.identifier,
    email: user.email,
    name:
      user.name ||
      [user.first_name, user.last_name].filter(Boolean).join(" ") ||
      undefined,
    role: user.role || user.role_name,
  };
}

export default logger;
